/**
 * Pakketten & prijzen — vaste bedragen excl. BTW (gelijk aan worker billing-plans).
 * Self-serve: Stripe Checkout via Account → Facturatie (tenant-beheerder).
 */
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { Check } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SeoMeta } from '../components/SeoMeta';
import { AcceptedPaymentStrip } from '../components/payment/AcceptedPaymentBrands';

const LAST_UPDATED = '29 maart 2026';

const TIERS = [
  {
    id: 'basis',
    name: 'Basis',
    tagline: 'ZZP en eerste serieuze gebruik',
    monthly: '€ 99',
    yearly: '€ 990',
    features: [
      '1 gebruiker · 2 dossiers',
      '1 gelijktijdige apparaat-sessie per gebruiker',
      '5 tekeninganalyses / maand',
      '10 exports / maand',
      'Prijsbibliotheek & calculatie · documentenbibliotheek per dossier',
      'Assistent (fair use)',
    ],
    highlight: false,
  },
  {
    id: 'plus',
    name: 'Plus',
    tagline: 'Klein team — meest gekozen',
    monthly: '€ 199',
    yearly: '€ 1.990',
    features: [
      '1–3 gebruikers · 5 dossiers',
      'Tot 3 apparaten tegelijk ingelogd per gebruiker',
      '20 analyses / maand',
      '40 exports / maand',
      'Prijsbibliotheek & calculatie · documentenbibliotheek per dossier',
      'Assistent',
    ],
    highlight: true,
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'Drukke aannemer, werkvoorbereiding',
    monthly: '€ 399',
    yearly: '€ 3.990',
    features: [
      '3–10 gebruikers · 15 dossiers',
      '60 analyses / maand',
      '120 exports / maand',
      'Prijsbibliotheek & calculatie · documentenbibliotheek per dossier',
      'API (quota)',
      'Prioriteit support',
    ],
    highlight: false,
  },
] as const;

export function Pricing() {
  const { user, isAccountManager } = useAuth();
  const authed = !!user?.authenticated;

  return (
    <div className="animate-page-enter">
      <SeoMeta
        path="/prijzen"
        title="Pakketten en prijzen"
        description="CalqTool™ abonnementen Basis, Plus en Pro: NL-prijsbibliotheek, onbeperkte calculatiedossiers, documenten per project en tekeninganalyse. Maand- of jaartarief — proefperiode waar beschikbaar."
      />
      <AnimatedSection delay={0.05}>
        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Pakketten' }]} />
        <h1 className="mb-3 text-2xl font-bold text-text">Pakketten en prijzen</h1>
        <p className="mb-4 max-w-3xl text-base leading-relaxed text-text">
          Kies het pakket dat bij je past — van ZZP tot drukbezette werkvoorbereiding. Je ziet hieronder precies wat je
          krijgt en wat het kost. Liever eerst uitproberen? Start gerust met de{' '}
          <Link to="/login?returnTo=%2Fprojects%2Fnew" className="font-medium text-primary hover:underline">
            proefperiode
          </Link>
          .
        </p>
        <div className="mb-6 rounded-xl border-2 border-primary/25 bg-primary/[0.06] p-4 dark:bg-primary/[0.09]">
          <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm font-medium text-text">
              <strong className="text-primary">Betaalmethoden</strong> — iDEAL, Bancontact, kaarten, Apple Pay, Google Pay
              en meer.{' '}
              <Link to="/betaalmethoden" className="font-medium text-primary underline-offset-2 hover:underline">
                Meer uitleg
              </Link>
            </p>
          </div>
          <div className="overflow-x-auto pb-1">
            <AcceptedPaymentStrip className="min-w-max justify-start sm:min-w-0 sm:flex-wrap" />
          </div>
        </div>
        <div className="mb-6 max-w-3xl rounded-xl border border-border bg-surface-card/80 p-4 text-sm leading-relaxed text-text-muted shadow-sm">
          <p className="font-medium text-text">Zo regel je je abonnement</p>
          <ul className="mt-2 list-inside list-disc space-y-1.5">
            <li>
              <strong className="text-text">Online betalen:</strong> in de app onder{' '}
              <strong className="text-text">Account → Facturatie</strong> (als je de{' '}
              <strong className="text-text">accountbeheerder</strong> van je organisatie bent: meestal de eerste
              registrant). Je betaalt veilig via onze betaalpartner — bijvoorbeeld met iDEAL of kaart.
            </li>
            <li>
              <strong className="text-text">Inloggen op meerdere apparaten:</strong> per gebruikersaccount geldt een
              maximum gelijktijdige sessies (Basis 1, Plus 3, Pro 5). Bij een nieuwe login op een extra apparaat kunnen
              oudere sessies automatisch worden beëindigd — zie ook Account → Inloggen &amp; apparaten.
            </li>
            <li>
              <strong className="text-text">Meer mensen op één abonnement:</strong> tijdens de proefperiode werk je alleen
              zelf in je bedrijfsaccount. Zodra je een pakket hebt gekozen, kun je collega’s uitnodigen via{' '}
              <strong className="text-text">Account → Team &amp; uitnodigingen</strong>. Zij openen de link uit de e-mail —
              niet opnieuw “account aanmaken” op de website, want dat start een tweede, apart bedrijfsaccount.
            </li>
            <li>
              <strong className="text-text">Prijzen op deze pagina</strong> zijn voor{' '}
              <strong className="text-text">zakelijke gebruik</strong> en <strong className="text-text">exclusief btw</strong>.
              Een jaarabonnement geeft ongeveer <strong className="text-text">16,7% voordeel</strong> t.o.v. twaalf keer de
              maandprijs.
            </li>
            <li>
              <strong className="text-text">Enterprise of maatwerk?</strong> Neem{' '}
              <Link to="/contact?onderwerp=enterprise" className="font-medium text-primary hover:underline">
                contact
              </Link>{' '}
              op — dan maken we een passend voorstel.
            </li>
          </ul>
          <p className="mt-3 text-xs text-text-muted">Laatst bijgewerkt: {LAST_UPDATED}</p>
        </div>

        <div className="mb-10 max-w-5xl overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/[0.1] via-surface-card to-surface-card p-5 shadow-sm sm:p-6">
          <p className="text-center text-sm text-text sm:text-base">
            <span className="font-semibold text-primary">Tip:</span> veel teams kiezen{' '}
            <span className="font-semibold text-text">Plus</span> — genoeg ruimte voor meerdere dossiers en exports per
            maand. Je kunt later altijd opschalen of verlagen.
          </p>
        </div>

        <div className="mb-10 grid max-w-5xl gap-6 md:grid-cols-3">
          {TIERS.map((t) => (
            <div
              key={t.id}
              className={`relative flex flex-col rounded-xl border bg-surface-card p-6 shadow-sm ${
                t.highlight
                  ? 'border-2 border-primary shadow-lg ring-2 ring-primary/10 md:scale-[1.02]'
                  : 'border-border'
              }`}
            >
              {t.highlight && (
                <span className="absolute right-3 top-3 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                  Populair
                </span>
              )}
              <h2 className="text-lg font-semibold text-text">{t.name}</h2>
              <p className="mt-1 text-sm text-text-muted">{t.tagline}</p>
              <p className="mt-4 text-2xl font-bold text-primary">{t.monthly}</p>
              <p className="text-xs text-text-muted">per maand · {t.yearly}/jaar excl. btw</p>
              <ul className="mt-4 flex-1 space-y-2 text-sm text-text-muted">
                {t.features.map((f) => (
                  <li key={f} className="flex gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mb-10 max-w-5xl overflow-x-auto rounded-lg border border-border bg-surface-card">
          <table className="w-full min-w-[640px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-border bg-surface">
                <th className="p-3 font-semibold text-text"> </th>
                <th className="p-3 font-semibold text-text">Basis</th>
                <th className="p-3 font-semibold text-text">Plus</th>
                <th className="p-3 font-semibold text-text">Pro</th>
                <th className="p-3 font-semibold text-text">Enterprise</th>
              </tr>
            </thead>
            <tbody className="text-text-muted">
              <tr className="border-b border-border">
                <td className="p-3 font-medium text-text">Prijs / maand (excl. btw)</td>
                <td className="p-3">€ 99</td>
                <td className="p-3">€ 199</td>
                <td className="p-3">€ 399</td>
                <td className="p-3">Op aanvraag</td>
              </tr>
              <tr className="border-b border-border">
                <td className="p-3 font-medium text-text">Prijs / jaar (excl. btw)</td>
                <td className="p-3">€ 990</td>
                <td className="p-3">€ 1.990</td>
                <td className="p-3">€ 3.990</td>
                <td className="p-3">—</td>
              </tr>
              <tr className="border-b border-border">
                <td className="p-3 font-medium text-text">Gebruikers (seats)</td>
                <td className="p-3">1</td>
                <td className="p-3">1–3</td>
                <td className="p-3">3–10</td>
                <td className="p-3">Contract</td>
              </tr>
              <tr className="border-b border-border">
                <td className="p-3 font-medium text-text">Actieve dossiers tegelijk</td>
                <td className="p-3">2</td>
                <td className="p-3">5</td>
                <td className="p-3">15</td>
                <td className="p-3">Contract</td>
              </tr>
              <tr className="border-b border-border">
                <td className="p-3 font-medium text-text">Tekeninganalyses / maand (per run)</td>
                <td className="p-3">5</td>
                <td className="p-3">20</td>
                <td className="p-3">60</td>
                <td className="p-3">Contract</td>
              </tr>
              <tr className="border-b border-border">
                <td className="p-3 font-medium text-text">Exports (PDF + spreadsheet) / maand</td>
                <td className="p-3">10</td>
                <td className="p-3">40</td>
                <td className="p-3">120</td>
                <td className="p-3">Contract</td>
              </tr>
              <tr className="border-b border-border">
                <td className="p-3 font-medium text-text">Prijsbibliotheek &amp; calculatie</td>
                <td className="p-3">✓</td>
                <td className="p-3">✓</td>
                <td className="p-3">✓</td>
                <td className="p-3">✓</td>
              </tr>
              <tr className="border-b border-border">
                <td className="p-3 font-medium text-text">Documentenbibliotheek per dossier (upload, hernoemen, hergebruik in analyse)</td>
                <td className="p-3">✓</td>
                <td className="p-3">✓</td>
                <td className="p-3">✓</td>
                <td className="p-3">✓</td>
              </tr>
              <tr className="border-b border-border">
                <td className="p-3 font-medium text-text">Assistent</td>
                <td className="p-3">✓ (fair use)</td>
                <td className="p-3">✓</td>
                <td className="p-3">✓</td>
                <td className="p-3">✓</td>
              </tr>
              <tr className="border-b border-border">
                <td className="p-3 font-medium text-text">API-toegang</td>
                <td className="p-3">—</td>
                <td className="p-3">Optioneel</td>
                <td className="p-3">✓ (quota)</td>
                <td className="p-3">Custom</td>
              </tr>
              <tr>
                <td className="p-3 font-medium text-text">Support</td>
                <td className="p-3">E-mail</td>
                <td className="p-3">E-mail 1–2 wd</td>
                <td className="p-3">Prioriteit</td>
                <td className="p-3">SLA</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mb-10 max-w-3xl rounded-lg border border-primary/25 bg-primary/5 p-5">
          <h2 className="mb-2 font-semibold text-text">Proefperiode</h2>
          <p className="text-sm text-text-muted">
            <strong className="text-text">7 dagen</strong> na registratie, met gebruikslimieten (richtlijn: max.{' '}
            <strong className="text-text">2</strong> tekeninganalyses, <strong className="text-text">1</strong> actief
            dossier, <strong className="text-text">3</strong> exports totaal — tenzij anders in de app vermeld). Einde
            zodra limieten of de 7 dagen bereikt zijn — <strong className="text-text">wat eerder komt</strong>. Daarna
            is een betaald abonnement nodig. Als <strong className="text-text">tenant-beheerder</strong> kun je direct
            afrekenen via <Link to="/account/billing" className="font-medium text-primary hover:underline">Facturatie</Link>{' '}
            (Stripe). Details in de{' '}
            <Link to="/terms" className="font-medium text-primary hover:underline">
              algemene voorwaarden
            </Link>
            .
          </p>
        </div>

        <div className="mb-10 max-w-3xl rounded-lg border border-border bg-surface-card p-5">
          <h2 className="mb-2 font-semibold text-text">Tekeninganalyse &amp; documentenbibliotheek</h2>
          <p className="text-sm text-text-muted">
            Elke keer dat je op <strong className="text-text">Analyseren</strong> drukt, telt dat als{' '}
            <strong className="text-text">één tekeninganalyse</strong> tegen je maand- of proeflimiet. Bestanden in de
            documentenbibliotheek van een dossier kun je <strong className="text-text">opnieuw aanvinken</strong> onder
            Tekening analyseren — zonder ze opnieuw te uploaden. Zie ook{' '}
            <Link to="/help/tekening" className="font-medium text-primary hover:underline">
              Tekening &amp; PDF
            </Link>
            .
          </p>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-4">
            <Link
              to="/login?returnTo=%2Fprojects%2Fnew"
              className="inline-flex rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white hover:bg-primary-alt"
            >
              Account maken / inloggen
            </Link>
            {authed && isAccountManager ? (
              <Link
                to="/account/billing"
                className="inline-flex rounded-lg border-2 border-primary bg-primary/5 px-5 py-2.5 text-sm font-medium text-primary hover:bg-primary/10"
              >
                Abonnement afsluiten of wijzigen
              </Link>
            ) : null}
            {!authed ? (
              <Link
                to="/login?returnTo=%2Faccount%2Fbilling"
                className="inline-flex rounded-lg border border-border bg-surface-card px-5 py-2.5 text-sm font-medium text-text hover:border-primary"
              >
                Inloggen om af te rekenen (beheerder)
              </Link>
            ) : null}
            <Link
              to="/contact?onderwerp=enterprise"
              className="inline-flex rounded-lg border border-border bg-surface-card px-5 py-2.5 text-sm font-medium text-text hover:border-primary"
            >
              Enterprise: neem contact op
            </Link>
            <Link to="/" className="inline-flex text-sm font-medium text-primary hover:underline">
              ← Home
            </Link>
          </div>
          {!authed ? (
            <p className="max-w-2xl text-xs text-text-muted">
              Ben je al beheerder? Log in en ga naar <strong className="text-text">Facturatie</strong> om een plan te
              kiezen en met Stripe af te rekenen.
            </p>
          ) : null}
        </div>
      </AnimatedSection>
    </div>
  );
}
